import { useEffect, useState } from 'react';

import { LDFlags } from '../constants/flags';
import { FALLBACK_PAYOFF_CREDENTIALS } from '../constants/lienholders';
import { LienholderRequirement } from '../gql/generated/graphql';
import { usePayoffIntelligence } from './useAutoFetchPayoff/usePayoffIntelligence';
import useFlag from './useFlag';

type MarketplacePayoffEligibility = {
  loading: boolean;
  shouldRender: boolean;
  missingFields: LienholderRequirement[];
  requiredFields: LienholderRequirement[];
};

const SSN_STRENGTH: Partial<Record<LienholderRequirement, number>> = {
  [LienholderRequirement.Ssn]: 9,
  [LienholderRequirement.LastSixSsn]: 6,
  [LienholderRequirement.LastFourSsn]: 4,
};

const DOB_STRENGTH: Partial<Record<LienholderRequirement, number>> = {
  [LienholderRequirement.Dob]: 2,
  [LienholderRequirement.DobYear]: 1,
};

const slotOf = (field: LienholderRequirement): string =>
  SSN_STRENGTH[field] ? 'ssn' : DOB_STRENGTH[field] ? 'dob' : field;

const strengthOf = (field: LienholderRequirement): number =>
  SSN_STRENGTH[field] ?? DOB_STRENGTH[field] ?? 0;

const mergePayoffFields = (
  piMissing: LienholderRequirement[],
  piRequired: LienholderRequirement[],
  fallback: LienholderRequirement[],
): LienholderRequirement[] => {
  const onFileAtOrAbove = (field: LienholderRequirement) =>
    piRequired.some(
      (required) =>
        !piMissing.includes(required) &&
        slotOf(required) === slotOf(field) &&
        strengthOf(required) >= strengthOf(field),
    );

  const needed = [...piMissing, ...fallback.filter((field) => !onFileAtOrAbove(field))];

  const strongestPerSlot = new Map<string, LienholderRequirement>();
  needed.forEach((field) => {
    const strongest = strongestPerSlot.get(slotOf(field));
    if (!strongest || strengthOf(field) > strengthOf(strongest)) {
      strongestPerSlot.set(slotOf(field), field);
    }
  });

  return Array.from(strongestPerSlot.values());
};

export const useMarketplacePayoffEligibility = (
  dealId: string,
  isListed: boolean,
  hasExistingPayoff: boolean,
  lienholderSlug?: string | null,
): MarketplacePayoffEligibility => {
  const hideListPrice = useFlag(LDFlags.HIDE_MARKETPLACE_LIST_PRICE);
  const { checkLienholderSupportedForPI } = usePayoffIntelligence();

  const [loading, setLoading] = useState(true);
  const [piSupport, setPiSupport] = useState<{
    isSupported: boolean;
    requiredFields: LienholderRequirement[];
    missingRequiredFields: LienholderRequirement[];
  }>({ isSupported: false, requiredFields: [], missingRequiredFields: [] });

  useEffect(() => {
    if (!hideListPrice || !dealId || hasExistingPayoff) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;
    const run = async () => {
      const response = await checkLienholderSupportedForPI(Number.parseInt(dealId, 10));
      if (mounted) {
        setPiSupport({
          isSupported: response.isSupported,
          requiredFields: response.requiredFields,
          missingRequiredFields: response.missingRequiredFields,
        });
        setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [hideListPrice, dealId, hasExistingPayoff, checkLienholderSupportedForPI]);

  const fallbackFields = (lienholderSlug && FALLBACK_PAYOFF_CREDENTIALS[lienholderSlug]) || [];
  const missingFields = mergePayoffFields(
    piSupport.missingRequiredFields,
    piSupport.requiredFields,
    fallbackFields,
  );
  const requiredFields = Array.from(new Set([...piSupport.requiredFields, ...missingFields]));

  const shouldRender = hideListPrice && !isListed && !hasExistingPayoff && missingFields.length > 0;

  return {
    loading,
    shouldRender,
    missingFields,
    requiredFields,
  };
};

export default useMarketplacePayoffEligibility;
